import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  getUserDeeplinks,
  createDeeplink,
  updateDeeplink,
  deleteDeeplink,
  toggleDeeplinkDisabled,
  type CreateDeeplinkInput,
  type UpdateDeeplinkInput,
} from '@/services/deeplinks';
import type { Deeplink } from '@/types';

export const useDeeplinks = () => {
  const { user } = useAuth();
  const [deeplinks, setDeeplinks] = useState<Deeplink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeeplinks = useCallback(async () => {
    if (!user) {
      setDeeplinks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getUserDeeplinks(user.uid);
      setDeeplinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch deeplinks');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDeeplinks();
  }, [fetchDeeplinks]);

  const create = async (input: Omit<CreateDeeplinkInput, 'userId'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const newDeeplink = await createDeeplink({
        ...input,
        userId: user.uid,
      });
      setDeeplinks((prev) => [newDeeplink, ...prev]);
      return newDeeplink;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create deeplink';
      setError(message);
      throw err;
    }
  };

  const update = async (id: string, input: UpdateDeeplinkInput) => {
    try {
      const updatedDeeplink = await updateDeeplink(id, input);
      setDeeplinks((prev) =>
        prev.map((d) => (d.id === id ? updatedDeeplink : d))
      );
      return updatedDeeplink;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update deeplink';
      setError(message);
      throw err;
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDeeplink(id);
      setDeeplinks((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete deeplink';
      setError(message);
      throw err;
    }
  };

  const toggleDisabled = async (id: string) => {
    try {
      const updatedDeeplink = await toggleDeeplinkDisabled(id);
      setDeeplinks((prev) =>
        prev.map((d) => (d.id === id ? updatedDeeplink : d))
      );
      return updatedDeeplink;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle deeplink status';
      setError(message);
      throw err;
    }
  };

  return {
    deeplinks,
    loading,
    error,
    create,
    update,
    remove,
    toggleDisabled,
    refresh: fetchDeeplinks,
  };
};
